import { ArrowDropDown, Check, EditOff, Label, Policy, Refresh, Style } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { MouseEvent, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DocumentType, PersonSmallDTO, TagReadDTO } from '../../api/dto';
import { useAdminRole } from '../../hooks/useAdminRole';
import { useDeviceType } from '../../hooks/useDeviceType';
import { copyToClipboard } from '../../lib/clipboard';
import { formatDateTime } from '../../lib/date';
import { formatVersionedId } from '../../lib/records/documents/versioned';
import { DocumentStatus } from '../../lib/status';
import { AuditData } from '../audit/AuditData';
import { ImpactHelmet, ImpactHelmetProps } from '../ImpactHelmet';
import { StatusChip } from '../StatusChip';
import { DocumentButtonsStack } from './DocumentButtonsStack';

interface DocumentTitleProps {
    documentId: number | null;
    documentVersion?: { masterId: number; versionNumber: number };
    title: string;
    creation: boolean;
}

export const DocumentTitle: React.FC<DocumentTitleProps> = ({ documentId, documentVersion, title, creation }) => {
    const intl = useIntl();
    const [tooltipCopied, setTooltipCopied] = useState(false);

    const handleClick = (e: MouseEvent<HTMLElement>) => {
        const textToCopy = documentVersion
            ? formatVersionedId(documentVersion.masterId, documentVersion.versionNumber)
            : documentId?.toString() ?? '';

        const selection = window.getSelection();
        if (selection) {
            const range = document.createRange();
            range.selectNodeContents(e.currentTarget);
            if (e.currentTarget.textContent !== null && e.currentTarget.textContent.endsWith(textToCopy)) {
                range.setStart(e.currentTarget.firstChild!, title.length - textToCopy.length);
            }
            selection.removeAllRanges();
            selection.addRange(range);
        }

        copyToClipboard(textToCopy).then((success) => {
            if (success) {
                setTooltipCopied(true);
            }
        });
    };

    return documentId !== null ? (
        <Tooltip
            title={intl.formatMessage({
                id: !tooltipCopied ? 'document.action.copyId.question' : 'document.action.copyId.feedback',
            })}
            arrow
        >
            <Typography
                sx={{
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    transition: 'text-shadow 0.2s ease-in-out',
                    ':hover': {
                        textShadow: '1px 1px 10px rgba(0, 0, 0, 0.3)',
                    },
                }}
                onClick={creation !== undefined ? handleClick : undefined}
                onMouseEnter={() => setTooltipCopied(false)}
                onMouseLeave={() => {
                    const selection = window.getSelection();
                    if (tooltipCopied && selection) {
                        selection.removeAllRanges();
                    }
                }}
            >
                {title}
            </Typography>
        </Tooltip>
    ) : (
        <Typography sx={{ whiteSpace: 'nowrap' }}>{title}</Typography>
    );
};

interface DocumentVersionSelectProps {
    currentVersionNumber: number;
    versions: { versionNumber: number; status: DocumentStatus }[];
    onSelect: (versionNumber: number) => void;
    currentVersionFormatter?: (versionNumber: number) => string;
    versionFormatter?: (versionNumber: number) => string;
    noWarning?: boolean;
}

const DocumentVersionSelect: React.FC<DocumentVersionSelectProps> = ({
    currentVersionNumber,
    versions,
    onSelect,
    currentVersionFormatter,
    versionFormatter,
}) => {
    const intl = useIntl();
    const { isMobile } = useDeviceType();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleOptionClick = (versionNumber: number) => {
        if (versionNumber !== currentVersionNumber) {
            onSelect(versionNumber);
        }
        handleClose();
    };
    const sortedVersions = useMemo(
        () => versions.slice().sort((a, b) => b.versionNumber - a.versionNumber),
        [versions]
    );
    return (
        <>
            {versions.length > 1 && (
                <Button size="small" color="inherit" onClick={handleClick} endIcon={<ArrowDropDown />}>
                    {!isMobile
                        ? currentVersionFormatter
                            ? currentVersionFormatter(currentVersionNumber)
                            : intl.formatMessage({ id: 'document.versionN' }, { version: currentVersionNumber })
                        : currentVersionNumber}
                </Button>
            )}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        style: {
                            maxHeight: 48 * 4.5,
                        },
                    },
                }}
            >
                {sortedVersions.map((option) => (
                    <MenuItem
                        key={option.versionNumber}
                        selected={option.versionNumber === currentVersionNumber}
                        onClick={() => handleOptionClick(option.versionNumber)}
                        sx={{ justifyContent: 'flex-end' }}
                    >
                        <ListItemIcon sx={{ flexGrow: 1 }}>
                            {option.versionNumber === currentVersionNumber && <Check />}
                        </ListItemIcon>
                        <Stack direction="row" spacing={2}>
                            <StatusChip {...option.status} size="small" />
                            <span>
                                {versionFormatter ? versionFormatter(option.versionNumber) : option.versionNumber}
                            </span>
                        </Stack>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

interface DocumentEditabilityChipProps {
    editable: boolean;
}

const DocumentEditabilityChip: React.FC<DocumentEditabilityChipProps> = ({ editable }) => {
    const intl = useIntl();
    return !editable ? (
        <Chip icon={<EditOff />} variant="outlined" label={intl.formatMessage({ id: 'document.cannotEdit' })} />
    ) : null;
};

interface DocumentMaskChipProps {
    mask?: string;
}

const DocumentMaskChip: React.FC<DocumentMaskChipProps> = ({ mask }) => {
    const intl = useIntl();
    return (
        <Chip
            icon={<Style />}
            variant="outlined"
            size="small"
            label={`${intl.formatMessage({ id: 'document.mask' })}: ${
                mask !== undefined ? mask : intl.formatMessage({ id: 'document.mask.default' })
            }`}
        />
    );
};

interface DocumentHeaderBaseProps {
    documentType: DocumentType | null;
    documentId: number | null;
    Main?: React.ReactNode;
    Left?: React.ReactNode;
    Right?: React.ReactNode;
    icon: React.ReactNode;
    title: string;
    shortTitle?: string;
    version?: DocumentVersionSelectProps;
    status?: DocumentStatus;
    creation?: { createdBy: PersonSmallDTO | null; createdDate: string | Date | null };
    editable: boolean;
    isLoading: boolean;
    onRefreshClick?: () => void;
    extraWarning?: string;
    mask?: string;
    showTags?: boolean;
    tags?: TagReadDTO[];
}

type DocumentHeaderProps = DocumentHeaderBaseProps & ImpactHelmetProps;

export const DocumentHeader = (props: DocumentHeaderProps): React.ReactElement => {
    const {
        documentType,
        documentId,
        Main,
        Left,
        Right,
        icon,
        title,
        shortTitle,
        version,
        status,
        creation,
        editable,
        isLoading,
        onRefreshClick,
        extraWarning,
        mask,
        showTags,
        tags,
    } = props;
    const intl = useIntl();
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));
    const { isMobile } = useDeviceType();
    const isOlderVersion = useMemo(
        () => (version ? version.versions.some((v) => v.versionNumber > version.currentVersionNumber) : false),
        [version]
    );
    const [openAuditDialog, setOpenauditDialog] = useState(false);
    const isAdmin = useAdminRole();

    const renderRefreshButton = () => (
        <Tooltip title={intl.formatMessage({ id: 'document.action.refresh' })}>
            <span>
                <IconButton size="small" onClick={onRefreshClick} disabled={isLoading}>
                    <Refresh />
                </IconButton>
            </span>
        </Tooltip>
    );

    const renderAuditButton = () =>
        isAdmin &&
        documentId !== null && (
            <>
                <Tooltip title={intl.formatMessage({ id: 'document.action.audit' })}>
                    <span>
                        <IconButton size="small" onClick={() => setOpenauditDialog(true)}>
                            <Policy />
                        </IconButton>
                    </span>
                </Tooltip>
                {openAuditDialog && !!documentType && (
                    <AuditData
                        documentType={documentType}
                        documentId={documentId}
                        open={openAuditDialog}
                        onClose={() => setOpenauditDialog(false)}
                    />
                )}
            </>
        );

    return (
        <>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
                sx={{ px: 2, pb: 1, minHeight: 42 }}
            >
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={{ xs: 1, md: 4 }}
                    alignItems={{ md: 'center' }}
                    justifyContent="space-between"
                    overflow="hidden"
                    width="100%"
                >
                    <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center" overflow="hidden">
                        <Stack direction="row" spacing={1} alignItems="center">
                            {icon}
                            {
                                <DocumentTitle
                                    documentId={documentId}
                                    documentVersion={
                                        'documentVersion' in props && props.documentVersion
                                            ? {
                                                  masterId: props.documentVersion.masterId,
                                                  versionNumber: props.documentVersion.versionNumber,
                                              }
                                            : undefined
                                    }
                                    title={title}
                                    creation={!!creation}
                                />
                            }
                            {!!version && <DocumentVersionSelect {...version} />}
                        </Stack>
                        {status !== undefined && <StatusChip {...status} size="small" sx={{ minWidth: 0 }} />}
                        {documentType === DocumentType.ACTIVITY && <DocumentMaskChip mask={mask} />}

                        {showTags && !isMobile && documentType === DocumentType.ACTIVITY && (
                            <Stack
                                sx={{
                                    mb: 1,
                                    px: 2,
                                    display: {
                                        sm: 'none',
                                        md: 'none',
                                        lg: 'block',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        flexWrap: 'nowrap',
                                        gap: 1,
                                        overflowX: 'auto',
                                        py: 0.5,
                                    }}
                                >
                                    {tags?.map((tag) => (
                                        <Chip
                                            key={tag.id}
                                            icon={<Label />}
                                            label={tag.name}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                fontWeight: 500,
                                                px: 0.5,
                                                borderRadius: '999px',
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Stack>
                        )}
                    </Stack>
                    {creation !== undefined && creation.createdBy !== null && creation.createdDate && (
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: { xs: 9, sm: '0.75rem' },
                                direction: { xs: 'rtl', sm: 'ltr' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                textAlign: 'left',
                            }}
                        >
                            <FormattedMessage
                                id="document.createdBy"
                                values={{
                                    person: creation.createdBy.searchLabel,
                                    date: formatDateTime(new Date(creation.createdDate), intl),
                                }}
                            />
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" spacing={1}>
                    {!isMobile && renderAuditButton()}
                    {!isMobile && onRefreshClick !== undefined && <Box minHeight={34}>{renderRefreshButton()}</Box>}
                </Stack>
            </Stack>
            <Divider />
            <Container disableGutters sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: -1, sm: 0 }, px: 2 }}>
                <Stack direction="row" alignItems="center" sx={{ minHeight: 32 }}>
                    <DocumentButtonsStack disabled={isLoading} flexGrow>
                        <Stack direction="column" flexGrow={1} spacing={{ xs: 1, sm: 2 }}>
                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                                <Stack direction="row" spacing={1} alignItems="baseline">
                                    {!isXs && <DocumentEditabilityChip editable={editable} />}
                                    {Left}
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="baseline">
                                    {Right}
                                    {isMobile && renderAuditButton()}
                                    {isMobile && onRefreshClick !== undefined && renderRefreshButton()}
                                </Stack>
                            </Stack>
                            {isOlderVersion && !version?.noWarning && (
                                <Alert severity="warning">{intl.formatMessage({ id: 'document.oldVersion' })}</Alert>
                            )}
                            {!!extraWarning && <Alert severity="warning">{extraWarning}</Alert>}
                            {isXs && <DocumentEditabilityChip editable={editable} />}
                            {Main}
                        </Stack>
                    </DocumentButtonsStack>
                </Stack>
            </Container>
            <ImpactHelmet
                title={shortTitle ?? title}
                {...(documentType !== null
                    ? {
                          documentType,
                          documentId,
                          documentVersion: 'documentVersion' in props ? props.documentVersion : undefined,
                      }
                    : {})}
            />
        </>
    );
};
